import { useEffect, useMemo, useState } from 'react'
import Boton from '../components/Boton'
import { tablasApi } from '../api/tablas'
import { colors, font, text, radius, space, sx } from '../design/tokens'

const PER_PAGE = 50

/** Formatea un valor de celda para mostrarlo (null, jsonb, booleanos, texto largo). */
function celda(v) {
  if (v === null || v === undefined) {
    return <span style={{ color: colors.textPlh, fontStyle: 'italic' }}>null</span>
  }
  if (typeof v === 'boolean') {
    return <span style={{ color: v ? colors.ok : colors.err, fontWeight: 600 }}>{v ? 'true' : 'false'}</span>
  }
  if (typeof v === 'object') {
    const s = JSON.stringify(v)
    return <span title={s} style={{ color: colors.textSec }}>{s.length > 80 ? s.slice(0, 80) + '…' : s}</span>
  }
  const s = String(v)
  return s.length > 120 ? <span title={s}>{s.slice(0, 120) + '…'}</span> : s
}

const fmtNum = (n) => Number(n).toLocaleString('es-AR')

export default function TablasPage() {
  const [tab, setTab] = useState('explorador')

  return (
    <div>
      <div style={{ marginBottom: space.xl }}>
        <h1 style={{ fontSize: text.title + 6, fontWeight: 700, color: colors.text, margin: 0 }}>Tablas</h1>
        <p style={{ fontSize: text.sec, color: colors.textSec, margin: '4px 0 0' }}>
          Explorador de la base — <strong>solo lectura</strong>. Las consultas no pueden modificar datos.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: space.xs, borderBottom: `1px solid ${colors.border}`, marginBottom: space.xl }}>
        {[['explorador', 'Explorador'], ['sql', 'Consola SQL']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: text.body, fontWeight: tab === id ? 600 : 500,
              color: tab === id ? colors.link : colors.textSec,
              borderBottom: `2px solid ${tab === id ? colors.link : 'transparent'}`,
              marginBottom: -1,
            }}
          >{label}</button>
        ))}
      </div>

      {tab === 'explorador' ? <Explorador /> : <ConsolaSql />}
    </div>
  )
}

/* ───────────────────────────── Explorador ───────────────────────────── */

function Explorador() {
  const [tablas, setTablas]   = useState([])
  const [buscar, setBuscar]   = useState('')
  const [sel, setSel]         = useState(null)

  const [data, setData]       = useState(null)
  const [cargando, setCargando] = useState(false)
  const [page, setPage]       = useState(1)
  const [orden, setOrden]     = useState(null)
  const [dir, setDir]         = useState('asc')
  const [filtroCol, setFiltroCol] = useState('')
  const [filtroVal, setFiltroVal] = useState('')
  const [filtroAplicado, setFiltroAplicado] = useState({ col: '', val: '' })

  useEffect(() => {
    tablasApi.listar().then(({ data }) => setTablas(data.tablas || []))
  }, [])

  // Recarga de filas cuando cambia tabla / página / orden / filtro aplicado.
  // El fetch vive en una función (no setState directo en el cuerpo del efecto).
  useEffect(() => {
    if (!sel) return
    let vivo = true
    const cargar = () => {
      setCargando(true)
      tablasApi.ver(sel, {
        page, per_page: PER_PAGE, orden, dir,
        filtro_col: filtroAplicado.col, filtro: filtroAplicado.val,
      })
        .then(({ data }) => { if (vivo) setData(data) })
        .finally(() => { if (vivo) setCargando(false) })
    }
    cargar()
    return () => { vivo = false }
  }, [sel, page, orden, dir, filtroAplicado])

  const abrir = (t) => {
    setSel(t); setPage(1); setOrden(null); setDir('asc')
    setFiltroCol(''); setFiltroVal(''); setFiltroAplicado({ col: '', val: '' })
  }

  const ordenarPor = (col) => {
    if (orden === col) { setDir(d => d === 'asc' ? 'desc' : 'asc') }
    else { setOrden(col); setDir('asc') }
    setPage(1)
  }

  const aplicarFiltro = () => { setFiltroAplicado({ col: filtroCol, val: filtroVal }); setPage(1) }
  const limpiarFiltro = () => { setFiltroCol(''); setFiltroVal(''); setFiltroAplicado({ col: '', val: '' }); setPage(1) }

  const listaFiltrada = useMemo(() => {
    const q = buscar.trim().toLowerCase()
    return q ? tablas.filter(t => t.tabla.toLowerCase().includes(q)) : tablas
  }, [tablas, buscar])

  const inputStyle = {
    fontFamily: font.sans, fontSize: text.body, padding: '6px 12px',
    border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, outline: 'none',
  }

  return (
    <div style={{ display: 'flex', gap: space.xl, alignItems: 'flex-start' }}>
      {/* Lista de tablas */}
      <div style={{ width: 280, flexShrink: 0 }}>
        <input
          value={buscar}
          onChange={e => setBuscar(e.target.value)}
          placeholder={`Buscar entre ${tablas.length} tablas…`}
          style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: space.sm }}
        />
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden', maxHeight: '70vh', overflowY: 'auto' }}>
          {listaFiltrada.map(t => (
            <button
              key={t.tabla}
              onClick={() => abrir(t.tabla)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: space.sm,
                width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
                padding: '7px 10px', borderBottom: `1px solid ${colors.borderSoft}`,
                background: sel === t.tabla ? colors.linkBg : colors.bg,
              }}
            >
              <span style={{ fontFamily: font.mono, fontSize: 12.5, color: sel === t.tabla ? colors.link : colors.text, fontWeight: sel === t.tabla ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.tabla}</span>
              <span style={{ fontFamily: font.mono, fontSize: 11, color: colors.textMeta, ...sx.mono, flexShrink: 0 }}>~{fmtNum(t.filas_estimadas)}</span>
            </button>
          ))}
          {listaFiltrada.length === 0 && (
            <div style={{ padding: space.lg, color: colors.textSec, fontSize: text.sec }}>Sin coincidencias.</div>
          )}
        </div>
      </div>

      {/* Datos de la tabla seleccionada */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!sel && (
          <div style={{ ...sx.card, color: colors.textSec, fontSize: text.body }}>
            Elegí una tabla de la izquierda para ver sus filas.
          </div>
        )}

        {sel && (
          <div>
            {/* Toolbar de filtro */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.sm, alignItems: 'center', marginBottom: space.md }}>
              <strong style={{ fontFamily: font.mono, fontSize: 14, color: colors.text }}>{sel}</strong>
              <select value={filtroCol} onChange={e => setFiltroCol(e.target.value)} style={inputStyle}>
                <option value="">— filtrar columna —</option>
                {(data?.columnas || []).map(c => <option key={c.nombre} value={c.nombre}>{c.nombre}</option>)}
              </select>
              <input
                value={filtroVal}
                onChange={e => setFiltroVal(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && aplicarFiltro()}
                placeholder="contiene…"
                disabled={!filtroCol}
                style={{ ...inputStyle, width: 200, opacity: filtroCol ? 1 : 0.5 }}
              />
              <Boton variante="primary" sm onClick={aplicarFiltro} disabled={!filtroCol}>Filtrar</Boton>
              {(filtroAplicado.col) && <Boton variante="ghost" sm onClick={limpiarFiltro}>Limpiar</Boton>}
              <span style={{ marginLeft: 'auto', fontSize: text.sec, color: colors.textSec, ...sx.mono }}>
                {data ? `${data.estimado ? '~' : ''}${fmtNum(data.total)} filas` : '…'}
              </span>
            </div>

            {/* Tabla */}
            <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'auto', maxHeight: '64vh' }}>
              <table style={{ ...sx.table, minWidth: '100%' }}>
                <thead>
                  <tr>
                    {(data?.columnas || []).map(c => (
                      <th
                        key={c.nombre}
                        onClick={() => ordenarPor(c.nombre)}
                        title={`${c.tipo}${c.nullable === 'NO' ? ' · NOT NULL' : ''} — click para ordenar`}
                        style={{ ...sx.th, cursor: 'pointer', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 }}
                      >
                        {c.nombre}{orden === c.nombre ? (dir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cargando && (
                    <tr><td colSpan={data?.columnas?.length || 1} style={{ ...sx.td, color: colors.textSec, padding: space.lg }}>Cargando…</td></tr>
                  )}
                  {!cargando && data?.filas?.map((row, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
                      {data.columnas.map(c => (
                        <td key={c.nombre} style={{ ...sx.td, padding: '6px 16px', fontFamily: font.mono, fontSize: 12, whiteSpace: 'nowrap', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {celda(row[c.nombre])}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {!cargando && data && data.filas.length === 0 && (
                    <tr><td colSpan={data.columnas.length} style={{ ...sx.td, color: colors.textSec, padding: space.lg }}>Sin filas.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginador */}
            <div style={{ display: 'flex', alignItems: 'center', gap: space.md, marginTop: space.md }}>
              <Boton variante="ghost" sm onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>‹ Anterior</Boton>
              <span style={{ fontSize: text.sec, color: colors.textSec, ...sx.mono }}>
                Página {page}{data ? ` · filas ${(page - 1) * PER_PAGE + 1}–${(page - 1) * PER_PAGE + data.filas.length}` : ''}
              </span>
              <Boton variante="ghost" sm onClick={() => setPage(p => p + 1)} disabled={!data || data.filas.length < PER_PAGE}>Siguiente ›</Boton>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* ───────────────────────────── Consola SQL ───────────────────────────── */

function ConsolaSql() {
  const [sql, setSql]       = useState('SELECT localidad, COUNT(*)\nFROM clientes\nGROUP BY 1\nORDER BY 2 DESC')
  const [res, setRes]       = useState(null)
  const [err, setErr]       = useState('')
  const [corriendo, setCorriendo] = useState(false)

  const ejecutar = async () => {
    setErr(''); setCorriendo(true); setRes(null)
    try {
      const { data } = await tablasApi.consultar(sql)
      setRes(data)
    } catch (e) {
      setErr(e.response?.data?.errors?.sql?.[0] || e.response?.data?.message || 'No se pudo ejecutar la consulta.')
    } finally {
      setCorriendo(false)
    }
  }

  return (
    <div>
      <div style={{ marginBottom: space.sm }}>
        <textarea
          value={sql}
          onChange={e => setSql(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) ejecutar() }}
          spellCheck={false}
          rows={6}
          style={{
            width: '100%', boxSizing: 'border-box', fontFamily: font.mono, fontSize: 13,
            padding: space.lg, border: `1px solid ${colors.textPlh}`, borderRadius: radius.md,
            background: colors.bgSoft, color: colors.text, outline: 'none', resize: 'vertical', lineHeight: 1.5,
          }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: space.md, marginBottom: space.lg }}>
        <Boton variante="primary" sm onClick={ejecutar} disabled={corriendo}>{corriendo ? 'Ejecutando…' : '▶ Ejecutar'}</Boton>
        <span style={{ fontSize: text.meta, color: colors.textMeta }}>⌘/Ctrl + Enter · solo SELECT · máx 1.000 filas · timeout 15 s</span>
        {res && <span style={{ marginLeft: 'auto', fontSize: text.sec, color: colors.textSec, ...sx.mono }}>{fmtNum(res.total)} filas · {res.ms} ms{res.topeado ? ' · topeado' : ''}</span>}
      </div>

      {err && (
        <div style={{ background: colors.errBg, color: colors.err, padding: space.lg, borderRadius: radius.md, fontSize: text.body, fontFamily: font.mono, marginBottom: space.lg, whiteSpace: 'pre-wrap' }}>{err}</div>
      )}

      {res && (
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'auto', maxHeight: '60vh' }}>
          <table style={{ ...sx.table, minWidth: '100%' }}>
            <thead>
              <tr>{res.columnas.map(c => <th key={c} style={{ ...sx.th, whiteSpace: 'nowrap', position: 'sticky', top: 0 }}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {res.filas.map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
                  {res.columnas.map(c => (
                    <td key={c} style={{ ...sx.td, padding: '6px 16px', fontFamily: font.mono, fontSize: 12, whiteSpace: 'nowrap', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>{celda(row[c])}</td>
                  ))}
                </tr>
              ))}
              {res.filas.length === 0 && (
                <tr><td colSpan={res.columnas.length || 1} style={{ ...sx.td, color: colors.textSec, padding: space.lg }}>La consulta no devolvió filas.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
